import { useState, type FormEvent } from 'react';
import { useParams } from '@tanstack/react-router';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { CreditCard, FlaskConical, Lock } from 'lucide-react';
import { PageContainer } from '@/shared/components/layout/PageContainer';
import { AppBadge } from '@/shared/components/ui/AppBadge/AppBadge';
import { AppButton } from '@/shared/components/ui/AppButton/AppButton';
import { AppCard } from '@/shared/components/ui/AppCard/AppCard';
import { EmptyState } from '@/shared/components/feedback/EmptyState';
import { LoadingSpinner } from '@/shared/components/feedback/LoadingSpinner/LoadingSpinner';
import { AdPlatform } from '@/shared/types/enums';
import { ROUTES } from '@/app/routes';
import { useCampaign } from '../../api/campaigns-queries';
import { useCampaignPayment } from '../../api/checkout-queries';
import {
  useAuthorizeCampaignPayment,
  useCancelCampaignPayment,
  useCaptureCampaignPayment,
} from '../../api/checkout-mutations';
import { checkoutEnabled, getStripe } from '../../logic/stripe';
import {
  META_PLACEMENT_OPTIONS,
  type CampaignPayment,
  type MetaPlacement,
  type PaymentAuthorization,
} from '../../types/checkout.types';
import {
  PAYMENT_STATUS_COPY,
  formatCents,
  isOpenHold,
  placementLabel,
  totalDiscountCents,
} from './CampaignCheckoutPage.logic';
import styles from './CampaignCheckoutPage.module.scss';

/**
 * Isolated Stripe checkout for a campaign. Not linked from the wizard or the
 * launch flow: reached only by URL (`/promote/campaigns/:id/checkout`) while
 * `VITE_STRIPE_PUBLISHABLE_KEY` is set. The "Simulate launch outcome" panel
 * stands in for the launch flow to capture or release the hold.
 */
export function CampaignCheckoutPage() {
  const { id = '' } = useParams({ strict: false }) as { id?: string };
  const { data: campaign, isLoading } = useCampaign(id);
  const { data: payment } = useCampaignPayment(id);
  const [placements, setPlacements] = useState<MetaPlacement[]>(['FACEBOOK', 'INSTAGRAM']);
  const [authorization, setAuthorization] = useState<PaymentAuthorization | null>(null);
  const authorize = useAuthorizeCampaignPayment(id);

  if (!checkoutEnabled) {
    return (
      <PageContainer title="Checkout">
        <EmptyState
          title="Checkout is not available"
          description="Set VITE_STRIPE_PUBLISHABLE_KEY to enable the campaign checkout."
        />
      </PageContainer>
    );
  }
  if (isLoading) return <LoadingSpinner />;
  if (!campaign) {
    return (
      <PageContainer title="Checkout">
        <EmptyState title="Campaign not found" description="Check the campaign link." />
      </PageContainer>
    );
  }

  const isMeta = campaign.platform === AdPlatform.META;
  const hasOpenHold = isOpenHold(payment);
  const canStart = !hasOpenHold && (!isMeta || placements.length > 0);

  const togglePlacement = (value: MetaPlacement) => {
    setPlacements((current) =>
      current.includes(value)
        ? current.filter((p) => p !== value)
        : META_PLACEMENT_OPTIONS.map((o) => o.value).filter(
            (p) => p === value || current.includes(p),
          ),
    );
  };

  return (
    <PageContainer
      title="Checkout"
      subtitle={campaign.name}
      breadcrumbs={[
        { label: 'Campaigns', href: ROUTES.campaigns },
        { label: campaign.name, href: ROUTES.campaignDetail.replace('$id', campaign.id) },
        { label: 'Checkout' },
      ]}
    >
      <div className={styles.layout}>
        <div className={styles.main}>
          <AppCard className={styles.card}>
            <h2 className={styles.cardTitle}>Placements</h2>
            {isMeta ? (
              <>
                <p className={styles.hint}>Each placement is billed separately.</p>
                <fieldset className={styles.placements} disabled={!!authorization || hasOpenHold}>
                  <legend className={styles.visuallyHidden}>Meta placements</legend>
                  {META_PLACEMENT_OPTIONS.map((option) => {
                    const checked = placements.includes(option.value);
                    return (
                      <label
                        key={option.value}
                        className={checked ? styles.placementSelected : styles.placement}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => togglePlacement(option.value)}
                        />
                        {option.label}
                      </label>
                    );
                  })}
                </fieldset>
                {placements.length === 0 && (
                  <p className={styles.error} role="alert">
                    Pick at least one placement.
                  </p>
                )}
              </>
            ) : (
              <p className={styles.hint}>{placementLabel(campaign.platform)}</p>
            )}
          </AppCard>

          <AppCard className={styles.card}>
            <h2 className={styles.cardTitle}>
              <CreditCard size={18} aria-hidden /> Payment
            </h2>
            {authorization?.clientSecret ? (
              <>
                <PriceSummary payment={authorization} />
                <Elements
                  stripe={getStripe()}
                  options={{
                    clientSecret: authorization.clientSecret,
                    locale: 'en',
                    appearance: {
                      theme: 'stripe',
                      variables: { fontSizeBase: '14px', spacingUnit: '3px' },
                    },
                  }}
                >
                  <CardForm authorization={authorization} onDone={() => setAuthorization(null)} />
                </Elements>
              </>
            ) : hasOpenHold && payment ? (
              <p className={styles.hint}>
                {formatCents(payment.amountCents, payment.currency)} is held on the card. Use the
                panel on the right to capture or release it.
              </p>
            ) : (
              <>
                <p className={styles.hint}>
                  We place a hold on the card now and charge it once the campaign is sent to the
                  platform.
                </p>
                <AppButton
                  onClick={() =>
                    authorize.mutate(isMeta ? placements : [], {
                      onSuccess: setAuthorization,
                    })
                  }
                  loading={authorize.isPending}
                  disabled={!canStart}
                >
                  Continue to payment
                </AppButton>
              </>
            )}
          </AppCard>
        </div>

        <aside className={styles.side}>
          <PaymentStatusCard payment={payment ?? null} />
          {hasOpenHold && payment && <OutcomeSimulator campaignId={id} payment={payment} />}
        </aside>
      </div>
    </PageContainer>
  );
}

function PriceSummary({ payment }: { payment: CampaignPayment }) {
  const discount = totalDiscountCents(payment.lineItems);
  return (
    <div className={styles.summary}>
      <ul className={styles.lineItems}>
        {payment.lineItems.map((item) => (
          <li key={item.placement} className={styles.row}>
            <span>{placementLabel(item.placement)}</span>
            <span>{formatCents(item.priceCents, payment.currency)}</span>
          </li>
        ))}
      </ul>
      <div className={styles.total}>
        <span>Total</span>
        <span>{formatCents(payment.amountCents, payment.currency)}</span>
      </div>
      {discount > 0 && (
        <p className={styles.note}>
          If the platform can&apos;t create the campaign, we charge{' '}
          {formatCents(payment.amountCents - discount, payment.currency)} instead.
        </p>
      )}
    </div>
  );
}

function CardForm({
  authorization,
  onDone,
}: {
  authorization: PaymentAuthorization;
  onDone: () => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const { refetch } = useCampaignPayment(authorization.campaignId);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!stripe || !elements) return;
    setError(null);
    setConfirming(true);
    // Cards that need 3DS open Stripe's challenge in place; no redirect.
    const result = await stripe.confirmPayment({ elements, redirect: 'if_required' });
    setConfirming(false);

    if (result.error) {
      setError(result.error.message ?? 'Your card could not be authorized.');
      return;
    }
    if (result.paymentIntent?.status === 'requires_capture') {
      // The webhook flips our row to AUTHORIZED; give it a moment, then refresh.
      await new Promise((resolve) => setTimeout(resolve, 1500));
      await refetch();
      onDone();
      return;
    }
    setError('Your card could not be authorized. Try another card.');
  };

  return (
    <form className={styles.form} onSubmit={handleSubmit} noValidate>
      <PaymentElement options={{ layout: 'tabs' }} />
      {error && (
        <p className={styles.error} role="alert">
          {error}
        </p>
      )}
      <AppButton type="submit" loading={confirming} disabled={!stripe || !elements}>
        <Lock size={14} aria-hidden />
        Authorize {formatCents(authorization.amountCents, authorization.currency)}
      </AppButton>
    </form>
  );
}

function PaymentStatusCard({ payment }: { payment: CampaignPayment | null }) {
  if (!payment) {
    return (
      <AppCard className={styles.card}>
        <h2 className={styles.cardTitle}>Payment status</h2>
        <p className={styles.hint}>No payment yet.</p>
      </AppCard>
    );
  }
  const copy = PAYMENT_STATUS_COPY[payment.status];
  return (
    <AppCard className={styles.card}>
      <div className={styles.statusHeader}>
        <h2 className={styles.cardTitle}>Payment status</h2>
        <AppBadge variant={copy.variant}>{copy.label}</AppBadge>
      </div>
      <dl className={styles.facts}>
        <dt>{payment.status === 'PENDING' ? 'Amount' : 'Authorized'}</dt>
        <dd>{formatCents(payment.amountCents, payment.currency)}</dd>
        {payment.discountCents > 0 && (
          <>
            <dt>Discount</dt>
            <dd>−{formatCents(payment.discountCents, payment.currency)}</dd>
          </>
        )}
        <dt>Charged</dt>
        <dd>
          {payment.capturedCents === null
            ? '—'
            : formatCents(payment.capturedCents, payment.currency)}
        </dd>
        {payment.outcome && (
          <>
            <dt>Launch outcome</dt>
            <dd>{payment.outcome}</dd>
          </>
        )}
      </dl>
      {payment.failureReason && payment.status === 'PENDING' && (
        <p className={styles.error}>{payment.failureReason}</p>
      )}
    </AppCard>
  );
}

function OutcomeSimulator({
  campaignId,
  payment,
}: {
  campaignId: string;
  payment: CampaignPayment;
}) {
  const capture = useCaptureCampaignPayment(campaignId);
  const cancel = useCancelCampaignPayment(campaignId);
  const busy = capture.isPending || cancel.isPending;
  const discounted = payment.amountCents - totalDiscountCents(payment.lineItems);

  return (
    <AppCard className={styles.card}>
      <h2 className={styles.cardTitle}>
        <FlaskConical size={18} aria-hidden /> Simulate launch outcome
      </h2>
      <p className={styles.hint}>
        The checkout is not wired into the launch yet. Settle the hold as the launch would.
      </p>
      <div className={styles.simulator}>
        <AppButton
          onClick={() => capture.mutate('SUCCESS')}
          loading={capture.isPending && capture.variables === 'SUCCESS'}
          disabled={busy}
        >
          Launch succeeded · charge {formatCents(payment.amountCents, payment.currency)}
        </AppButton>
        <AppButton
          variant="secondary"
          onClick={() => capture.mutate('CREATE_FAILED')}
          loading={capture.isPending && capture.variables === 'CREATE_FAILED'}
          disabled={busy}
        >
          Launch failed · charge {formatCents(discounted, payment.currency)}
        </AppButton>
        <AppButton
          variant="ghost"
          onClick={() => cancel.mutate()}
          loading={cancel.isPending}
          disabled={busy}
        >
          Release hold
        </AppButton>
      </div>
    </AppCard>
  );
}
